import { css } from '@emotion/react';
import { type FC } from 'react';
import { useDispatch } from 'react-redux';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { ExportReportTemplateRendererComponent } from '~/exportReport/templateRenderer/exportReportTemplateRenderer.component';
import { useExportReportData } from '~/exportReport/useExportReportData';
import { useExportReportParams } from '~/exportReport/useExportReportParams';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { MapExportReportPreviewHeaderComponent } from '~/map/page/exportReportPreview/mapExportReportPreviewHeader.component';
import { createReportCreateRequest } from '~/store/frontendState/createReport/createReport.actionCreators';
import { exportReportDisable } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.actionCreators';
import type { ExportReportLocationParams } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.state';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';

const contentStyle = css({
  padding: 20,
  maxWidth: 1000,
  margin: '0 auto',
});

const googleMapStyle = css({
  width: '50%',
  aspectRatio: '386 / 234',
});

type MapExportReportPreviewLocationProps = Readonly<{
  params: ExportReportLocationParams;
  template: ReportTemplate;
}>;

export const MapExportReportPreviewLocationComponent: FC<MapExportReportPreviewLocationProps> = (props) => {
  const dispatch = useDispatch();
  const mapId = useMapIdSelector();

  const spreadsheetData = useLatLngSpreadsheetData();
  const rowData = spreadsheetData.getRow(props.params.data.markerId);
  const { tableColumns } = useExportReportParams();
  const { getDemographicValue, getDemographicLabel } = useExportReportData(props.template, rowData ?? null);

  const closePdfSummary = () => {
    dispatch(exportReportDisable());
  };

  const onPrintClick = () => {
    if (!mapId) {
      return;
    }

    dispatch(createReportCreateRequest(mapId));
  };

  if (!rowData) {
    return null;
  }

  return (
    <div>
      <MapExportReportPreviewHeaderComponent onCloseClick={closePdfSummary} onPrintClick={onPrintClick} />

      <div css={contentStyle}>
        <ExportReportTemplateRendererComponent
          template={props.template}
          tableColumns={tableColumns}
          summaryProps={{
            latLng: {
              lat: rowData.lat,
              lng: rowData.lng,
            },
            heading: props.params.data.summary.heading,
            semiHeading: props.params.data.summary.semiHeading,
            description: props.params.data.summary.description,
          }}
          mapProps={{
            center: rowData,
            css: googleMapStyle,
            type: ExportReportType.Location,
          }}
          getDemographicLabel={getDemographicLabel}
          getDemographicValue={getDemographicValue}
        />
      </div>
    </div>
  );
};
